import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AutocompleteInput } from "../components/AutocompleteInput";
import { useAuth } from "../auth/AuthContext";

export type DashboardFilters = {
  bankAccountId?: number | null;
  categoryId?: number | null;
  costCenterId?: number | null;
  accountId?: number | null;
  from?: string;
  to?: string;
};

type SelectOption = { id: number; label: string };

type DashboardFiltersProps = {
  value: DashboardFilters;
  onChange: (value: DashboardFilters) => void;
};

export const DashboardFiltersBar: React.FC<DashboardFiltersProps> = ({
  value,
  onChange,
}) => {
  const { client } = useAuth();
  const [bankAccounts, setBankAccounts] = useState<SelectOption[]>([]);
  const [categories, setCategories] = useState<SelectOption[]>([]);
  const [costCenters, setCostCenters] = useState<SelectOption[]>([]);
  const [accountOption, setAccountOption] = useState<SelectOption | null>(null);

  useEffect(() => {
    client.get("/modules/bank-accounts?take=100&skip=0").then(async (res) => {
      if (!res.ok) return;
      const payload = (await res.json()) as {
        data: Array<{ id: number; bank: { name: string }; agency: string; accountNumber: string }>;
      };
      setBankAccounts(
        payload.data.map((item) => ({
          id: item.id,
          label: `${item.bank.name} ${item.agency}/${item.accountNumber}`,
        }))
      );
    });
    client.get("/modules/categories?take=100&skip=0").then(async (res) => {
      if (!res.ok) return;
      const payload = (await res.json()) as {
        data: Array<{ id: number; name: string; code?: string | null }>;
      };
      setCategories(
        payload.data.map((item) => ({
          id: item.id,
          label: `${item.code ?? ""} ${item.name}`.trim(),
        }))
      );
    });
    client.get("/modules/cost-centers?take=100&skip=0").then(async (res) => {
      if (!res.ok) return;
      const payload = (await res.json()) as {
        data: Array<{ id: number; name: string; code?: string | null }>;
      };
      setCostCenters(
        payload.data.map((item) => ({
          id: item.id,
          label: `${item.code ?? ""} ${item.name}`.trim(),
        }))
      );
    });
  }, [client]);

  const fetchAccounts = useCallback(
    async (query: string) => {
      const response = await client.get(
        `/modules/accounts?q=${encodeURIComponent(query)}&take=10&skip=0`
      );
      if (!response.ok) return [];
      const payload = (await response.json()) as {
        data: Array<{ id: number; code: string; name: string }>;
      };
      return payload.data.map((item) => ({
        id: item.id,
        label: `${item.code} - ${item.name}`,
      }));
    },
    [client]
  );

  const update = useCallback(
    (patch: Partial<DashboardFilters>) => {
      onChange({ ...value, ...patch });
    },
    [onChange, value]
  );

  const clearFilters = () => {
    setAccountOption(null);
    onChange({});
  };

  const activeCount = useMemo(
    () =>
      Object.values(value).filter((v) => v !== undefined && v !== null && v !== "").length,
    [value]
  );

  return (
    <div className="card shadow-sm mb-3" style={{ borderTop: "3px solid #0d6efd" }}>
      <div className="card-body py-2">
        <div className="row g-2 align-items-end">
          <div className="col-md-2">
            <label className="form-label form-label-sm mb-1">Conta bancaria</label>
            <select
              className="form-select form-select-sm"
              value={value.bankAccountId ?? ""}
              onChange={(e) =>
                update({ bankAccountId: e.target.value === "" ? null : Number(e.target.value) })
              }
            >
              <option value="">Todas</option>
              {bankAccounts.map((o) => (
                <option key={o.id} value={o.id}>{o.label}</option>
              ))}
            </select>
          </div>
          <div className="col-md-2">
            <label className="form-label form-label-sm mb-1">Categoria</label>
            <select
              className="form-select form-select-sm"
              value={value.categoryId ?? ""}
              onChange={(e) =>
                update({ categoryId: e.target.value === "" ? null : Number(e.target.value) })
              }
            >
              <option value="">Todas</option>
              {categories.map((o) => (
                <option key={o.id} value={o.id}>{o.label}</option>
              ))}
            </select>
          </div>
          <div className="col-md-2">
            <label className="form-label form-label-sm mb-1">Centro de custo</label>
            <select
              className="form-select form-select-sm"
              value={value.costCenterId ?? ""}
              onChange={(e) =>
                update({ costCenterId: e.target.value === "" ? null : Number(e.target.value) })
              }
            >
              <option value="">Todos</option>
              {costCenters.map((o) => (
                <option key={o.id} value={o.id}>{o.label}</option>
              ))}
            </select>
          </div>
          <div className="col-md-2">
            <AutocompleteInput
              label="Conta contabil"
              placeholder="Buscar conta"
              value={accountOption?.id ?? null}
              selectedLabel={accountOption?.label}
              fetchOptions={fetchAccounts}
              onSelect={(option) => {
                setAccountOption(option);
                update({ accountId: option?.id ?? null });
              }}
            />
          </div>
          <div className="col-md-2">
            <label className="form-label form-label-sm mb-1">De</label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={value.from ?? ""}
              onChange={(e) => update({ from: e.target.value })}
            />
          </div>
          <div className="col-md-1">
            <label className="form-label form-label-sm mb-1">Ate</label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={value.to ?? ""}
              onChange={(e) => update({ to: e.target.value })}
            />
          </div>
          <div className="col-md-1 d-flex align-items-end">
            {activeCount > 0 && (
              <button
                className="btn btn-outline-secondary btn-sm w-100"
                onClick={clearFilters}
                title={`${activeCount} filtro${activeCount !== 1 ? "s" : ""} ativo${activeCount !== 1 ? "s" : ""}`}
              >
                Limpar
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
